import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiAlertCircle,
  FiCheck,
  FiCheckCircle,
  FiEye,
  FiEyeOff,
  FiLayers,
  FiLock,
  FiMail,
  FiShield
} = FiIcons;

const BPA_LOGO_URL = 'https://cloud.sharemyimage.com/2026/07/26/logo_fixed_512_true_png.png';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [phase, setPhase] = useState('idle');
  const [error, setError] = useState('');

  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const busy = phase === 'submitting';
  const successful = phase === 'success';

  if (isAuthenticated && phase === 'idle') {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (busy || successful) return;

    if (!email.trim() || !password) {
      setError('Enter your email and password to continue.');
      return;
    }

    setPhase('submitting');
    setError('');

    try {
      await login(email, password);
      setPhase('success');
      await new Promise(resolve => window.setTimeout(resolve, 320));
      navigate('/', { replace: true });
    } catch (loginError) {
      console.error('Video Hub login failed:', loginError);

      const status = Number(loginError?.status || loginError?.response?.status || 0);
      if (status === 404) {
        setError('Admin authentication is not configured in PocketBase yet. Confirm the users auth collection during backend setup.');
      } else if (status === 0) {
        setError('Could not reach PocketBase. Check the backend URL or network connection.');
      } else {
        setError('The email or password is incorrect, or this account is not permitted to access Video Hub.');
      }
      setPhase('idle');
    }
  };

  return (
    <div className="login-page">
      <div className="login-pattern" aria-hidden="true" />

      <motion.div
        initial={{ opacity: 0, y: 16, scale: 0.99 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
        className="login-shell"
      >
        <section className="login-story" aria-label="BPA Video Hub overview">
          <div className="relative z-10 h-full flex flex-col">
            <div>
              <div className="login-story-accent mb-6" />
              <p className="text-sm font-semibold uppercase tracking-[0.18em] opacity-80">BPA Solutions</p>
              <h1 className="text-4xl font-bold tracking-tight mt-4 leading-tight">Training content, governed professionally.</h1>
              <p className="login-story-copy mt-5 text-base leading-7 max-w-md">
                Manage application-specific training videos, publishing controls and topics from one focused administration hub.
              </p>
            </div>

            <div className="mt-12 space-y-5">
              <div className="login-feature">
                <div className="login-feature-icon"><SafeIcon icon={FiLayers} className="w-4 h-4" /></div>
                <div>
                  <p className="font-semibold">Central video registry</p>
                  <p className="text-sm opacity-75 mt-1">Keep targeting, metadata and distribution rules together.</p>
                </div>
              </div>
              <div className="login-feature">
                <div className="login-feature-icon"><SafeIcon icon={FiShield} className="w-4 h-4" /></div>
                <div>
                  <p className="font-semibold">Controlled training access</p>
                  <p className="text-sm opacity-75 mt-1">Prepare secure app-specific libraries without exposing admin content.</p>
                </div>
              </div>
              <div className="login-feature">
                <div className="login-feature-icon"><SafeIcon icon={FiCheckCircle} className="w-4 h-4" /></div>
                <div>
                  <p className="font-semibold">Clear publishing workflow</p>
                  <p className="text-sm opacity-75 mt-1">Draft, review, publish and archive with deliberate governance.</p>
                </div>
              </div>
            </div>

            <p className="mt-auto pt-12 text-xs opacity-60">Authorized BPA Solutions administration only</p>
          </div>
        </section>

        <section className="login-form-panel">
          <div className="login-brand-logo-wrap mb-8">
            <img src={BPA_LOGO_URL} alt="BPA Solutions" className="login-brand-logo" />
          </div>

          <div className="mb-8">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] app-subtle">BPA Video Hub · Admin Console</p>
            <h2 className="login-title text-3xl font-bold tracking-tight mt-2">Welcome back</h2>
            <p className="login-copy mt-2 text-sm leading-6">Sign in to manage training content, topics and client application libraries.</p>
          </div>

          <form className="space-y-5" onSubmit={handleSubmit}>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                className="login-alert text-sm"
                role="alert"
              >
                <SafeIcon icon={FiAlertCircle} className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </motion.div>
            )}

            {successful && (
              <motion.div
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                className="login-alert login-success text-sm"
                role="status"
              >
                <SafeIcon icon={FiCheck} className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>Signed in securely. Opening your dashboard…</span>
              </motion.div>
            )}

            <div>
              <label htmlFor="login-email" className="login-label block text-xs font-semibold uppercase tracking-wider mb-2">Email</label>
              <div className="login-input-wrap">
                <SafeIcon icon={FiMail} className="login-input-icon w-5 h-5" />
                <input
                  id="login-email"
                  type="email"
                  required
                  autoComplete="email"
                  inputMode="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  className="login-input"
                  placeholder="admin@bpasolutions.au"
                  disabled={busy || successful}
                />
              </div>
            </div>

            <div>
              <label htmlFor="login-password" className="login-label block text-xs font-semibold uppercase tracking-wider mb-2">Password</label>
              <div className="login-input-wrap">
                <SafeIcon icon={FiLock} className="login-input-icon w-5 h-5" />
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="login-input"
                  placeholder="••••••••"
                  disabled={busy || successful}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(current => !current)}
                  className="login-icon-button"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  disabled={busy || successful}
                >
                  <SafeIcon icon={showPassword ? FiEyeOff : FiEye} className="h-5 w-5" />
                </button>
              </div>
            </div>

            <button type="submit" disabled={busy || successful} className="login-button">
              {busy ? (
                <>
                  <span className="auth-spinner !w-5 !h-5 !m-0 !border-2" aria-hidden="true" />
                  <span>Signing in…</span>
                </>
              ) : successful ? (
                <>
                  <SafeIcon icon={FiCheck} className="w-5 h-5" />
                  <span>Signed in</span>
                </>
              ) : (
                <span>Sign In</span>
              )}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-slate-800">
            <p className="login-footer text-xs leading-5">
              Your session is validated against PocketBase before protected content is shown.
            </p>
          </div>
        </section>
      </motion.div>

      <p className="login-footer absolute bottom-3 left-0 right-0 text-center text-[11px] px-4">
        © {new Date().getFullYear()} BPA Solutions. Authorized personnel only.
      </p>
    </div>
  );
};

export default Login;